import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

import { toPosAmount } from '@/components/pos/posCatalog'
import { isPackageCoveredLine } from '@/lib/pos/posPackageRedeem'
import { formatLineStatusLabel } from '@/components/pos/posLineStatus'
import { cn } from '@/lib/utils'

/**
 * Compact single-line ticket row. Tap to select and show operations on the right panel.
 *
 * @param {object} props
 * @param {import('@/types/api').SaleLine} props.line
 * @param {import('@/types/api').Stylist[]} props.stylists
 * @param {boolean} props.disabled
 * @param {boolean} [props.selected]
 * @param {boolean} [props.compact]
 * @param {() => void} [props.onSelect]
 * @param {() => void} [props.onRemove]
 */
export function PosCartLineEditor({
  line,
  stylists,
  disabled,
  selected = false,
  compact = false,
  onSelect,
  onRemove,
}) {
  const rowRef = useRef(null)
  const assignedStylist = stylists.find((stylist) => stylist.id === line.stylist_id)
  const packageTag = isPackageCoveredLine(line) ? 'Pkg' : ''
  const lineStatus = line.line_status ?? 'waiting'

  useEffect(() => {
    if (selected && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selected, line.id])

  return (
    <li
      ref={rowRef}
      className={cn(
        'pos-v2-ticket-line flex items-stretch',
        selected && 'pos-v2-ticket-line--selected',
        lineStatus === 'in_chair' && 'pos-v2-ticket-line--in_chair',
        lineStatus === 'done' && 'opacity-80',
      )}
    >
      <button
        type="button"
        className={cn('pos-v2-ticket-row min-w-0 flex-1', compact && 'pos-v2-ticket-row--compact')}
        disabled={disabled}
        onClick={onSelect}
      >
        <span className="pos-v2-ticket-cell pos-v2-ticket-cell--name truncate font-semibold">
          {line.description}
          {packageTag ? (
            <span className="pos-v2-ticket-tag ml-1 font-medium text-emerald-700">{packageTag}</span>
          ) : null}
        </span>
        <span className="pos-v2-ticket-cell truncate text-slate-500">
          {assignedStylist?.name ?? '—'}
        </span>
        {!compact ? (
          <span className="pos-v2-ticket-cell truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {formatLineStatusLabel(lineStatus)}
          </span>
        ) : null}
        {!compact ? (
          <>
            <span className="pos-v2-ticket-cell text-slate-500">
              {line.duration ? `${line.duration}m` : '—'}
            </span>
            <span className={cn('pos-v2-ticket-cell pos-v2-ticket-cell--num text-center font-mono font-bold', selected && 'text-primary')}>
              {line.quantity}
            </span>
            <span className="pos-v2-ticket-cell pos-v2-ticket-cell--num text-right font-mono text-slate-600">
              {toPosAmount(line.unit_price).toFixed(0)}
            </span>
          </>
        ) : (
          <span className={cn('pos-v2-ticket-cell pos-v2-ticket-cell--num text-center font-mono font-bold', selected && 'text-primary')}>
            {line.quantity}
          </span>
        )}
        <span className={cn('pos-v2-ticket-cell pos-v2-ticket-cell--num text-right font-mono font-bold', selected ? 'text-primary' : 'text-slate-800')}>
          {toPosAmount(line.line_total).toFixed(2)}
        </span>
      </button>
      {onRemove ? (
        <button
          type="button"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
          }}
          className="pos-v2-ticket-remove flex shrink-0 items-center justify-center border-l border-[#E5E7EB] px-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
          aria-label={`Remove ${line.description}`}
          title="Remove item"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </li>
  )
}
